import React, { useState } from 'react'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import { MdOutlineAutoAwesome } from 'react-icons/md'

const engagementsData = [
    { id: 1, title: 'Virtual Reality in Gaming Arena', img: '/assets/newvrifylogo1.png', desc: 'Immersive multiplayer VR gaming experiences designed for social play and competition.', category: 'Gaming' },
    { id: 2, title: 'Virtual Tours', img: '/assets/newvrifylogo2black.png', desc: 'Explore landmarks, homes, and destinations through guided, high-fidelity virtual tours.', category: 'Tourism' },
    { id: 3, title: 'Virtual Reality in Education', img: '/assets/newvrifylogo1.png', desc: 'Interactive learning journeys that make complex subjects easier to visualise and remember.', category: 'Education' },
    { id: 4, title: 'Virtual Reality in Fitness & Wellness', img: '/assets/newvrifylogo1.png', desc: 'Engaging fitness programs and wellness environments that turn exercise into an experience.', category: 'Wellness' },
    { id: 5, title: 'Virtual Reality in Corporate Training', img: '/assets/newvrifylogo2black.png', desc: 'Realistic simulations for teams learning procedures, safety, and product demonstrations.', category: 'Training' },
    { id: 6, title: 'Virtual Reality in Concerts', img: '/assets/newvrifylogo1.png', desc: 'Attend live music concerts and festivals in immersive virtual spaces.', category: 'Events' },
    { id: 7, title: 'Virtual Reality in Therapy Sessions', img: '/assets/newvrifylogo2black.png', desc: 'Calming, supportive environments for relaxation and guided therapeutic experiences.', category: 'Therapy' },
    { id: 8, title: 'Virtual Reality in Sports Training', img: '/assets/newvrifylogo1.png', desc: 'Enhance sports performance with interactive drills and immersive training scenarios.', category: 'Sports' },
    { id: 9, title: 'Virtual Social Events', img: '/assets/newvrifylogo1.png', desc: 'Connect with friends and communities through virtual parties, meetups, and events.', category: 'Social' }
]

const VrEngagements = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const cardsPerPage = 6

    const indexOfLastCard = currentPage * cardsPerPage
    const indexOfFirstCard = indexOfLastCard - cardsPerPage
    const currentCards = engagementsData.slice(indexOfFirstCard, indexOfLastCard)
    const totalPages = Math.ceil(engagementsData.length / cardsPerPage)

    return (
        <div>
            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 pt-16 md:pt-20 lg:pt-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.24),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.16),_transparent_22%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-8 pt-8 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="max-w-3xl">
                                <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">
                                    <MdOutlineAutoAwesome className="text-sm" />
                                    VR engagements
                                </p>
                                <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                    Explore immersive VR experiences across industries and audiences.
                                </h1>
                                <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                    From entertainment and education to wellness, training, and tourism, our engagement formats are designed to make each interaction feel premium and memorable.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {currentCards.map((engagement) => (
                                <ScrollReveal key={engagement.id}>
                                    <article className="flex h-full flex-col overflow-hidden rounded-[24px] border border-white/10 bg-slate-900/80 shadow-[0_24px_80px_rgba(15,23,42,0.45)] transition duration-300 hover:-translate-y-1 hover:border-yellow-300/80">
                                        <div className="relative h-48 overflow-hidden">
                                            <img src={engagement.img} alt={engagement.title} className="h-full w-full object-cover transition duration-500 hover:scale-105" loading="lazy" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/10 to-transparent" />
                                            <span className="absolute left-4 top-4 rounded-full bg-yellow-400 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-950">
                                                {engagement.category}
                                            </span>
                                        </div>

                                        <div className="flex flex-1 flex-col p-5">
                                            <h2 className="text-lg font-semibold text-white">{engagement.title}</h2>
                                            <p className="mt-3 flex-1 text-sm leading-7 text-slate-300">{engagement.desc}</p>
                                            <a href='/contactus' className="mt-5 inline-flex items-center justify-center rounded-full bg-yellow-400 px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-yellow-300">
                                                Discuss this engagement
                                            </a>
                                        </div>
                                    </article>
                                </ScrollReveal>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setCurrentPage(i + 1)}
                                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                        currentPage === i + 1
                                            ? 'bg-yellow-400 text-slate-950'
                                            : 'border border-white/10 bg-white/5 text-white hover:border-yellow-300 hover:text-yellow-200'
                                    }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                <div className="max-w-2xl">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-950">Ready to create</p>
                                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">Bring your next immersive experience to life with VRIFY.</h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">
                                        Tell us your audience, goals, and preferred format and we will help shape a polished, responsive engagement that fits your brand.
                                    </p>
                                </div>
                                <div className="mt-6 flex flex-wrap gap-3 lg:mt-0">
                                    <a href='/contactus' className='inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-300 transition hover:bg-slate-900'>
                                        Contact us
                                    </a>
                                    <a href='/virtual-reality' className='inline-flex items-center justify-center rounded-full border border-slate-950 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-yellow-300'>
                                        Explore services
                                    </a>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default VrEngagements
